import h from 'hyperscript';
import getVideoId from 'get-video-id';
import Cookies from 'js-cookie';
import { COOKIESTATUS } from 'javascripts/constants';
import Overlay from 'javascripts/utils/overlay';
import { t } from 'javascripts/utils/withTranslation';

export const getStart = (url) => {
  const params = new URLSearchParams(url.split('?')[1]);
  return params.get('start');
};

export const initializeIframe = ({ url, withCookie, container }) => {
  const isVideoLoadedBefore = container.querySelector('.figure__video-wrapper');
  const { id, service } = getVideoId(url);
  const start = getStart(url);
  let $iframeContent = null;
  // Construct Youtube iframe player, add rel=0 to disable related videos
  if (id && service === 'youtube') {
    $iframeContent = h(
      '.figure__video-wrapper',
      h('iframe.figure__video-iframe', {
        src: `https://www.youtube${
          withCookie ? '' : '-nocookie'
        }.com/embed/${id}?rel=0&autoplay=1&start=${start || 0}`,
        allowFullscreen: true,
        msAllowFullscreen: true,
        webkitAllowFullscreen: true,
        mozAllowFullscreen: true,
      })
    );
  }

  // Construct Vimeo iframe player
  if (id && service === 'vimeo') {
    $iframeContent = h(
      '.figure__video-wrapper',
      h('iframe.figure__video-iframe', {
        src: `https://player.vimeo.com/video/${id}`,
        allowFullscreen: true,
        msAllowFullscreen: true,
        webkitAllowFullscreen: true,
        mozAllowFullscreen: true,
      })
    );
  }

  if (!isVideoLoadedBefore && $iframeContent) {
    container.appendChild($iframeContent, container);
  }
};

class FigureVideo {
  constructor($el, config) {
    this.config = {
      wrapperClass: 'figure__media',
      playLinkClass: 'figure__play',
      ...config,
    };
    this.$el = $el;
    this.$figure = this.$el.querySelector(`.${this.config.wrapperClass}`);
    this.$playLink = this.$el.querySelector(`.${this.config.playLinkClass}`);
    this.$ogPlayLink = this.$playLink;
    this.videoSource =
      this.$playLink?.href || this.$playLink?.getAttribute('data-video-source');
    this.config.videoSource = this.videoSource;
    this.overlayActive = false;
    this.cookieName = this.$el.getAttribute('data-cookie');

    if (this.$playLink && this.videoSource) {
      this.checkForCookie();
      this.initPlayButton();
    }

    this.Overlay = new Overlay({
      onOverlayInit: this.onInitOverlay,
      onDecline: this.onDeclineConsent,
      onConsent: this.onConsent,
      onGlobalConsent: this.onGlobalConsent,
      consentCookieName: this.cookieName,
      providerName: this.$el.getAttribute('data-provider'),
      $el: this.$el,
    });

    document.addEventListener('cookieAccepted', this.cookieChange);
  }

  cookieChange = () => {
    this.deconstructor();
    this.checkForCookie();
    this.initPlayButton();
  };

  initPlayButton = () => {
    const b = document.createElement('div');
    b.classList.add('figure__play');
    if (this.$playLink) {
      b.innerHTML = this.$playLink.innerHTML;
      b.setAttribute('data-video-source', this.config.videoSource);
      this.$playLink.parentNode?.replaceChild(b, this.$playLink);
      this.$playLink = b;
      this.$videoOverlay = this.$playLink.querySelector('.figure__overlay');
      this.$copyright = this.$playLink.querySelector('.figure__copyright');
      this.$mediaButton = this.$playLink.querySelector('.figure__media-button');

      this.$playLink.addEventListener('click', this.playVideo);
    }

    // Needed for correct keyboard navigation and not to nest a button inside another button
    if (this.$mediaButton) {
      const m = document.createElement('button');
      m.classList.add('media-button');
      m.classList.add('figure__media-button');
      m.innerHTML = this.$mediaButton.innerHTML;
      this.$mediaButton.parentNode.replaceChild(m, this.$mediaButton);
      this.$mediaButton = m;
      const accessabilityText = document.createElement('span');
      accessabilityText.classList.add('u-hidden-visually');
      accessabilityText.innerHTML = t('Video abspielen');
      this.$mediaButton.appendChild(accessabilityText);

      this.$mediaButton.addEventListener('click', this.playVideo);
    }
  };

  checkForCookie = () => {
    const { service } = getVideoId(this.config.videoSource || '');
    const neededCookie = Cookies.get(`bawu-${service}`) || null;

    if (neededCookie && neededCookie === COOKIESTATUS.ACCEPT) {
      this.$figure.classList.remove('figure__cookie-overlay-active');
      this.Overlay?.destroy();
    }
  };

  playVideo = (event) => {
    const { service } = getVideoId(this.config.videoSource || '');
    const neededCookie = Cookies.get(`bawu-${service}`) || null;

    if (!neededCookie || neededCookie !== COOKIESTATUS.ACCEPT) {
      // Init overlay only if not active yet
      if (!this.$figure.classList.contains('figure__cookie-overlay-active')) {
        this.Overlay.init();
        this.overlayActive = true;
        this.$figure.classList.add('figure__cookie-overlay-active');
      }
    } else {
      this.onConsent();
    }
  };

  onConsent = () => {
    this.$figure.classList.remove('figure__cookie-overlay-active');
    this.$figure.classList.add('figure__cookie-consent');
    this.Overlay.destroy();
    this.overlayActive = false;

    this.$figure = this.$el.querySelector(`.${this.config.wrapperClass}`);

    initializeIframe({
      url: this.config.videoSource,
      container: this.$figure,
      withCookie: false,
    });
  };

  onGlobalConsent = () => {
    initializeIframe({
      url: this.config.videoSource,
      container: this.$figure,
      withCookie: true,
    });

    this.Overlay.destroy();
    this.overlayActive = false;
  };

  deconstructor() {
    this.Overlay.destroy();
    this.overlayActive = false;
    this.$figure.classList.remove('figure__cookie-overlay-active');
    this.$figure.classList.remove('figure__cookie-consent');
    const $iframe = this.$figure.querySelector('.figure__video-wrapper');
    $iframe?.remove();
  }
}

export default FigureVideo;
